import clsxm from "@/lib/clsxm";
import {type FC, type ReactNode} from "react";

type TSegmentedOption = {
  className?: string;
  count?: number;
  icon?: ReactNode;
  iconPosition?: "after" | "before";
  label: string;
  onClick: () => void;
};

const SegmentedToggle: FC<{
  accessibilityLabel?: string;
  dataTestId?: string;
  className?: string;
  isDisabled?: boolean;
  options: TSegmentedOption[];
  selectedIndex?: number;
  titleClassName?: string;
  variant?: "default" | "elevated" | "outlined" | "small";
}> = ({
  accessibilityLabel,
  dataTestId,
  className,
  isDisabled,
  options,
  selectedIndex,
  titleClassName,
  variant = "default",
}) => {
  return (
    <div
      role={accessibilityLabel ? "radiogroup" : undefined}
      aria-label={accessibilityLabel}
      className={clsxm(
        "border-primary-light-solid flex grow items-center justify-center overflow-hidden border",
        variant === "default" && "h-15 rounded-lg",
        variant === "outlined" && "h-10 shrink-0 grow-0 border-0",
        variant === "small" && "h-10 max-w-96 rounded-full",
        variant === "elevated" && "bg-neutral-grey-light-dark h-12 gap-0.5 rounded-xl",
        className,
      )}>
      {options.map((option, index) => {
        const isSelected = index === selectedIndex;

        return (
          <div
            key={index}
            data-testid={dataTestId ? `${dataTestId}-option-${index}` : undefined}
            role={accessibilityLabel ? "radio" : undefined}
            aria-checked={accessibilityLabel ? isSelected : undefined}
            aria-disabled={accessibilityLabel ? isDisabled : undefined}
            tabIndex={accessibilityLabel ? (isSelected && !isDisabled ? 0 : -1) : undefined}
            onKeyDown={
              accessibilityLabel
                ? event => {
                    if (isDisabled) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      option.onClick();
                    } else if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) {
                      event.preventDefault();
                      const next =
                        event.key === "Home"
                          ? 0
                          : event.key === "End"
                            ? options.length - 1
                            : (index + (event.key === "ArrowRight" ? 1 : -1) + options.length) % options.length;
                      options[next].onClick();
                      (event.currentTarget.parentElement?.children[next] as HTMLElement)?.focus();
                    }
                  }
                : undefined
            }
            onClick={() => {
              if (!isDisabled) {
                option.onClick();
              }
            }}
            className={clsxm(
              "flex h-full flex-1 cursor-pointer items-center justify-center gap-1 px-1 py-2 text-center text-sm font-medium transition-colors sm:gap-2 sm:px-2.5",
              variant === "small" && "border-primary-light-solid",
              variant === "default" && "border-primary-sky",
              index !== 0 && variant !== "outlined" && "border-l",
              variant === "outlined" &&
                "border-neutral-grey-light-dark text-neutral-grey-dark hover:border-primary-dark-solid relative flex-none border px-3 font-semibold whitespace-nowrap hover:z-20 sm:px-3",
              variant === "outlined" && index === 0 && "rounded-l-sm",
              variant === "outlined" && index !== 0 && "-ml-px",
              variant === "outlined" && index === options.length - 1 && "rounded-r-sm",
              variant === "elevated" && "m-1 border-none transition-none max-sm:h-10 max-sm:py-px",
              isSelected
                ? clsxm(
                    "bg-primary-dark-solid text-white",
                    variant === "elevated" && "text-primary-dark-solid h-fit rounded-lg bg-white shadow-sm",
                    variant === "outlined" &&
                      "border-primary-dark-solid text-primary-dark-solid bg-primary-light-solid z-10",
                  )
                : clsxm(
                    variant === "default" && "text-neutral-black bg-white",
                    variant === "outlined" && "text-neutral-grey-dark bg-white",
                    variant === "elevated" && "text-neutral-grey-dark",
                    variant === "small" && "text-neutral-grey-light hover:bg-neutral-grey-lightest bg-white",
                  ),
              isDisabled && "bg-neutral-grey-lightest text-neutral-grey-light cursor-not-allowed",
              isDisabled && isSelected && "text-neutral-grey-dark bg-primary-light-solid",
              titleClassName,
              option.className,
            )}>
            {option.icon && option.iconPosition !== "after" && option.icon}
            {option.label}
            {option.icon && option.iconPosition === "after" && option.icon}
            {!!option.count && (
              <span
                className={clsxm(
                  "text-primary-dark-solid flex min-h-5 min-w-5 items-center justify-center rounded-full p-1 text-sm leading-0 font-semibold",
                  isSelected && "bg-neutral-grey-light-dark",
                )}>
                {option.count}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SegmentedToggle;
